import { UserGender } from '@mfw/common';
import { CollapsibleInfo, useIntl } from '@mfw/components';
import { FormAutocomplete } from '@mfw/forms';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import React from 'react';

import messages from './messages';

type FormatMessage = (descriptor: {
  id?: string;
  defaultMessage: string;
}) => string;

const GENDER_MESSAGES = {
  [UserGender.MALE]: messages.male,
  [UserGender.FEMALE]: messages.female,
};

/** A value the profile still offers (PO 2026-10-03: male or female only). */
export const isUserGender = (
  gender: null | string | undefined,
): gender is UserGender =>
  gender === UserGender.MALE || gender === UserGender.FEMALE;

/**
 * The label of a stored value; `null` for anything the profile no longer
 * offers (PO 2026-10-03: male or female only), so a display never shows a
 * raw or undefined value.
 */
export const genderLabel = (
  gender: null | string | undefined,
  formatMessage: FormatMessage,
) => (isUserGender(gender) ? formatMessage(GENDER_MESSAGES[gender]) : null);

export interface GenderFieldProps {
  'data-testid': string;
  name: string;
}

/**
 * The one gender picker (onboarding page, onboarding dialog, own profile):
 * male or female, with a one-line reason and the PO's full text behind "i".
 */
export const GenderField = ({
  'data-testid': testId,
  name,
}: GenderFieldProps) => {
  const { formatMessage } = useIntl();
  const options = React.useMemo(
    () =>
      [UserGender.MALE, UserGender.FEMALE].map((value) => ({
        label: formatMessage(GENDER_MESSAGES[value]),
        value,
      })),
    [formatMessage],
  );

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <FormAutocomplete
        label={formatMessage(messages.label)}
        title={formatMessage(messages.label)}
        data-testid={testId}
        total={options.length}
        options={options}
        name={name}
      />
      <CollapsibleInfo
        data-testid={`${testId}-info`}
        trigger={
          <Typography color="text.secondary" variant="caption">
            {formatMessage(messages.explanationShort)}
          </Typography>
        }
      >
        <Typography color="text.secondary" variant="caption" component="p">
          {formatMessage(messages.explanationFull)}
        </Typography>
      </CollapsibleInfo>
    </Box>
  );
};
